import React, { useEffect, useRef, useState, useCallback } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { CloseOutlined } from "@ant-design/icons";
import RequestHandler from "util/RequestHandler";
import useHideOnScroll from "util/hooks/useHideOnScroll";
import {
  trackBannerClick,
  resolveBannerTargetUrl,
  openBannerTarget,
} from "util/BannerClickActions";
import TahsiliteOzelBadge from "assets/images/tahsiLite - buton-badge.svg";
import "./BannerSlider.css";

const SimpleFadeSlider = ({ banners, onBannerClick, autoplaySpeed = 10000 }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (banners.length < 2) return undefined;
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % banners.length);
    }, autoplaySpeed);
    return () => clearInterval(interval);
  }, [banners.length, autoplaySpeed]);

  return (
    <div className="simple-fade-slider">
      {banners.map((banner, i) => (
        <div
          key={banner.id}
          className={`simple-fade-slider__slide ${i === activeIndex ? "simple-fade-slider__slide--active" : ""}`}
        >
          <button
            type="button"
            className="finekra-banner-button"
            onClick={() => onBannerClick(banner)}
            aria-label={banner.name || "banner"}
          >
            <img src={TahsiliteOzelBadge} alt="Tahsilite'a Özel" className="finekra-banner-badge" />
            <img
              className="finekra-banner-image"
              src={banner.imagePath}
              alt={banner.name || "banner"}
            />
          </button>
        </div>
      ))}
    </div>
  );
};

const BannerSlider = ({
  screenName,
  hideOnScroll = true,
  controlParameter = true,
  isLogin = true,
  firmId = null,
  showBanner = true,
  tenantId = null,
  closable = false,
  onClose,
  isLoginPage = false,
}) => {
  const [banners, setBanners] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [closed, setClosed] = useState(false);
  const sliderRef = useRef(null);
  const visible = useHideOnScroll({ threshold: 30 });
  const isVisible = hideOnScroll ? visible : true;

  useEffect(() => {
    let cancelled = false;

    if (!showBanner) {
      setBanners([]);
      setLoaded(true);
      return () => { cancelled = true; };
    }

    setLoaded(false);

    let url = "/Banner/GetActive";
    if (tenantId) {
      url += "?tenantId=" + tenantId;
    }

    RequestHandler.get(url)
      .then((response) => {
        if (cancelled) return;
        const list = Array.isArray(response) ? response : [];
        setBanners(list.filter((b) => b && b.imagePath));
        setLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setBanners([]);
        setLoaded(true);
      });

    return () => { cancelled = true; };
  }, [showBanner, tenantId]);

  const handleCloseClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setClosed(true);
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  const handleBannerClick = useCallback((banner) => {
    trackBannerClick({
      bannerId: banner.id,
      screenName,
      firmId,
    });
    const target = resolveBannerTargetUrl(banner, { isLogin });
    openBannerTarget(target);
  }, [screenName, firmId, isLogin]);

  if (!loaded || banners.length === 0 || closed) {
    return null;
  }

  const containerClass = [
    "finekra-banner-slider",
    isVisible ? "" : "finekra-banner-slider--hidden",
    screenName === "PosCollection"
      ? "finekra-banner-slider--with-bottom-gap"
      : "",
    closable ? "finekra-banner-slider--closable" : "",
  ]
    .filter(Boolean)
    .join(" ");

  if (isLoginPage) {
    return (
      <div className={containerClass}>
        {closable ? (
          <button type="button" className="finekra-banner-slider-close" onClick={handleCloseClick} aria-label="Kapat">
            <CloseOutlined />
          </button>
        ) : null}
        <SimpleFadeSlider banners={banners} onBannerClick={handleBannerClick} />
      </div>
    );
  }

  const settings = {
    dots: banners.length > 1,
    infinite: banners.length > 1,
    autoplay: banners.length > 1,
    autoplaySpeed: 10000,
    speed: 600,
    fade: true,
    arrows: false,
    pauseOnHover: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: false,
  };

  return (
    <div className={containerClass}>
      {closable ? (
        <button type="button" className="finekra-banner-slider-close" onClick={handleCloseClick} aria-label="Kapat">
          <CloseOutlined />
        </button>
      ) : null}
      <Slider ref={sliderRef} {...settings}>
        {banners.map((banner) => (
          <div key={banner.id} className="finekra-banner-slide">
            <button
              type="button"
              className="finekra-banner-button"
              onClick={() => handleBannerClick(banner)}
              aria-label={banner.name || "banner"}
            >
              <img src={TahsiliteOzelBadge} alt="Tahsilite'a Özel" className="finekra-banner-badge" />
              <img
                className="finekra-banner-image"
                src={banner.imagePath}
                alt={banner.name || "banner"}
              />
            </button>
          </div>
        ))}
      </Slider>
    </div>
  );
};

export default BannerSlider;
